import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface TableSelectColumn {
  field: string;
  header: string;
  width?: string;
  format?: (value: any) => string;
}

@Component({
  selector: 'app-table-select',
  templateUrl: './table-select.component.html',
  styleUrls: ['./table-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TableSelectComponent),
      multi: true,
    },
  ],
})
export class TableSelectComponent implements ControlValueAccessor {
  @Input() suggestions: any[] = [];
  @Input() columns: TableSelectColumn[] = [];
  @Input() optionLabel: string = 'label';
  @Input() placeholder: string = 'Selecione...';
  @Input() disabled: boolean = false;
  @Input() showClear: boolean = false;
  @Input() style: any = {};
  @Input() inputStyle: any = {};
  @Input() styleClass: string = '';
  @Input() dialogHeader: string = 'Selecionar';
  @Input() searchPlaceholder: string = 'Buscar...';
  @Input() emptyMessage: string = 'Nenhum resultado encontrado';
  @Input() loading: boolean = false;
  @Input() delay: number = 0;
  @Input() secondarySearchPlaceholder: string = '';
  @Input() secondarySearchLabel: string = '';

  @Output() completeMethod = new EventEmitter<{ query: string; secondaryQuery: string }>();
  @Output() onSelect = new EventEmitter<any>();
  @Output() onClear = new EventEmitter<void>();

  visible: boolean = false;
  searchQuery: string = '';
  secondarySearchQuery: string = '';
  pendingRow: any = null;
  value: any = null;

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};
  private debounceTimer: any = null;

  get displayLabel(): string {
    if (!this.value) return '';
    if (typeof this.value === 'string') return this.value;
    return this.value[this.optionLabel] ?? '';
  }

  openDialog(): void {
    if (this.disabled) return;
    this.pendingRow = this.value;
    this.searchQuery = '';
    this.secondarySearchQuery = '';
    this.visible = true;
    this.completeMethod.emit({ query: '', secondaryQuery: '' });
  }

  private emitSearch(): void {
    this.completeMethod.emit({ query: this.searchQuery, secondaryQuery: this.secondarySearchQuery });
  }

  onSearchInput(): void {
    if (!this.delay) {
      this.emitSearch();
      return;
    }
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.emitSearch(), this.delay);
  }

  selectRow(item: any): void {
    this.pendingRow = item;
  }

  isSelected(item: any): boolean {
    return this.pendingRow === item;
  }

  confirm(): void {
    if (this.pendingRow != null) {
      this.value = this.pendingRow;
      this.onChange(this.value);
      this.onTouched();
      this.onSelect.emit(this.value);
    }
    this.visible = false;
  }

  close(): void {
    this.visible = false;
    this.pendingRow = null;
  }

  clear(event: Event): void {
    event.stopPropagation();
    this.value = null;
    this.onChange(null);
    this.onTouched();
    this.onClear.emit();
  }

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
